import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom';
import { api } from '../services/api';
import { useAuth, roleHomePath } from '../context/AuthContext';
import { useBranding } from '../context/BrandingContext';

type Screen = 'login' | 'register' | 'mfa' | 'forgot' | 'forgotSent';

// While false, the Contractor tab of Create Account collects an application and emails it to
// support@box-runner.net instead of self-registering — lets us control onboarding pace early
// on. The automated POST /api/auth/register/contractor path below is fully intact; flip this
// back to true once ready to open contractor sign-up back up. Mirrors the same toggle in the
// mobile app (C:\brm\App.tsx).
const CONTRACTOR_SELF_REGISTER_ENABLED = false;

function DarkInput({ label, value, onChange, type = 'text', placeholder, required }: {
  label: string; value: string; onChange: (v: string) => void;
  type?: string; placeholder?: string; required?: boolean;
}) {
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === 'password';
  return (
    <div style={{ marginBottom: 24 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: 'var(--color-accent, #4ADE80)', letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 8 }}>
        {label}
      </label>
      <div style={{ position: 'relative' }}>
        <input
          type={isPassword && revealed ? 'text' : type}
          value={value}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          style={{
            width: '100%', background: 'transparent', border: 'none',
            borderBottom: '1px solid rgba(255,255,255,0.18)',
            color: '#fff', padding: isPassword ? '10px 40px 10px 8px' : '10px 0 10px 8px', fontSize: 15, outline: 'none',
            boxSizing: 'border-box', caretColor: 'var(--color-accent, #4ADE80)',
          }}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setRevealed(r => !r)}
            aria-label={revealed ? 'Hide password' : 'Show password'}
            style={{
              position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)',
              background: 'none', border: 'none', cursor: 'pointer', padding: 4,
              display: 'flex', alignItems: 'center', fontSize: 18, lineHeight: 1,
            }}
          >
            {revealed ? '🙈' : '👁'}
          </button>
        )}
      </div>
    </div>
  );
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const branding = useBranding();
  const contractorLabel = branding.contractorNoun || 'Contractor';
  const [searchParams] = useSearchParams();
  // Only ever honor our own public-estimate links — never an arbitrary redirect target, and
  // never for a Contractor/Admin/Vendor login (a customer-facing link redirect makes no sense
  // for those roles even if the query param is somehow present).
  const rawRedirect = searchParams.get('redirect');
  const redirectTo = rawRedirect && rawRedirect.startsWith('/estimate/') ? rawRedirect : null;

  const [screen, setScreen] = useState<Screen>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [regName, setRegName] = useState('');
  const [regPhone, setRegPhone] = useState('');
  const [regRole, setRegRole] = useState<'Customer' | 'Contractor'>('Customer');
  const [regVehicle, setRegVehicle] = useState('');

  const [pendingToken, setPendingToken] = useState('');
  const [otp, setOtp] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const roleHome = (role: string) =>
    (role === 'Customer' && redirectTo) ? redirectTo : roleHomePath(role);

  // Already signed in — e.g. the browser Back button landed on "/" after login. Skip the form
  // and forward to the portal instead of showing a login screen the user then has to click
  // Forward past. AuthContext hydrates synchronously from localStorage, so there's no flash.
  if (user?.role) return <Navigate to={roleHome(user.role)} replace />;

  const finishLogin = (resp: { token?: string | null; userId?: string | null; name?: string | null; email?: string | null; role?: string | null }) => {
    if (!resp.token || !resp.userId || !resp.name || !resp.email || !resp.role) return;
    login({ token: resp.token, userId: resp.userId, name: resp.name, email: resp.email, role: resp.role });
    // replace: true so the login page is dropped from history — Back should return to wherever
    // the user came from, never to a login screen while authenticated.
    navigate(roleHome(resp.role), { replace: true });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const resp = await api.loginCustomer(email, password);
      if (resp.mfaRequired && resp.pendingToken) { setPendingToken(resp.pendingToken); setScreen('mfa'); }
      else finishLogin(resp);
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleMfa = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { finishLogin(await api.verifyMfa(pendingToken, otp)); }
    catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try {
      const resp = regRole === 'Customer'
        ? await api.registerCustomer({ name: regName, email, password, phoneNumber: regPhone })
        : await api.registerContractor({ name: regName, email, password, phoneNumber: regPhone, vehicleDescription: regVehicle });
      login(resp);
      navigate(roleHome(regRole), { replace: true });
    } catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const handleForgot = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setLoading(true);
    try { await api.forgotPassword(email); setScreen('forgotSent'); }
    catch (err: any) { setError(err.message); }
    finally { setLoading(false); }
  };

  const submitBtn = (label: string) => (
    <button type="submit" disabled={loading} style={{
      width: '100%', padding: '14px', borderRadius: 10,
      background: loading ? `${(branding.buttonColor ?? branding.secondaryColor)}66` : `linear-gradient(135deg, ${branding.buttonColor ?? branding.primaryColor} 0%, ${branding.buttonColor ?? branding.secondaryColor} 100%)`,
      color: '#fff', fontWeight: 700, fontSize: 15, border: 'none',
      cursor: loading ? 'not-allowed' : 'pointer', letterSpacing: 0.3,
      boxShadow: loading ? 'none' : `0 4px 24px ${(branding.buttonColor ?? branding.primaryColor)}66`,
      transition: 'opacity 0.2s',
    }}>
      {loading ? 'Please wait…' : label}
    </button>
  );

  const backBtn = (to: Screen) => (
    <button type="button" onClick={() => { setScreen(to); setError(''); }}
      style={{ marginTop: 18, width: '100%', background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer' }}>
      ← Back to sign in
    </button>
  );

  const formPanel = (title: string, subtitle: React.ReactNode, content: React.ReactNode) => (
    <div style={{
      width: isMobile ? '100%' : 420,
      flexShrink: 0, background: '#0D1B2A',
      padding: isMobile ? '36px 24px 40px' : '44px 44px',
      overflowY: 'auto',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
    }}>
      <button type="button" onClick={() => navigate('/')}
        style={{ alignSelf: 'flex-start', marginBottom: 16, background: 'none', border: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 13, cursor: 'pointer', padding: 0 }}>
        ← Browse services
      </button>
      <div style={{ marginBottom: 28 }}>
        {branding.logoUrl
          ? <img src={branding.logoUrl} alt={branding.appName} style={{ height: isMobile ? 56 : 72, maxWidth: 260, objectFit: 'contain' }} />
          : <div style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 800, fontStyle: 'italic', fontSize: isMobile ? 24 : 28, letterSpacing: 2, color: branding.primaryColor }}>
              {branding.appName}
            </div>
        }
      </div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: '#fff', fontSize: isMobile ? 18 : 22, fontWeight: 800, margin: '0 0 6px 0', lineHeight: 1.2 }}>{title}</h1>
        <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.75)' }}>{subtitle}</div>
      </div>
      {error && (
        <div style={{ marginBottom: 20, padding: '10px 14px', background: 'rgba(192,57,43,0.15)', border: '1px solid rgba(192,57,43,0.35)', borderRadius: 8, color: '#FC8181', fontSize: 13 }}>
          {error}
        </div>
      )}
      {content}
    </div>
  );

  const layout = (title: string, subtitle: React.ReactNode, content: React.ReactNode) => {
    const hasSplash = !!branding.splashImageUrl;
    // TREK-130: detect custom branding by presence of tenant assets, not by comparing appName
    // against a hardcoded default — that breaks if the platform's own default name ever changes,
    // and checks the wrong thing (platform identity, not whether this tenant has branding set).
    const isCustomTenant = !!branding.logoUrl || hasSplash;

    const rightPanel = hasSplash
      ? <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 400,
            backgroundImage: `url(${branding.splashImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
      : isCustomTenant
        ? <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 400,
              background: `linear-gradient(135deg, ${branding.secondaryColor} 0%, ${branding.primaryColor} 100%)` }}>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 16 }}>
              {branding.logoUrl
                ? <img src={branding.logoUrl} alt={branding.appName} style={{ maxWidth: '65%', maxHeight: 140, objectFit: 'contain', filter: 'drop-shadow(0 4px 24px rgba(0,0,0,0.4))' }} />
                : <div style={{ fontSize: 32, fontWeight: 800, color: '#fff', letterSpacing: 1, textAlign: 'center', padding: '0 24px' }}>{branding.appName}</div>
              }
            </div>
          </div>
        : <div style={{ flex: 1, position: 'relative', overflow: 'hidden', minHeight: 400 }}>
            <video autoPlay muted loop playsInline
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
              src="/login-bg.mp4"
            />
            <div style={{ position: 'absolute', inset: 0, background: 'rgba(8, 24, 16, 0.25)' }} />
          </div>;

    const mobileBg = hasSplash
      ? { backgroundImage: `url(${branding.splashImageUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : isCustomTenant
        ? { background: `linear-gradient(135deg, ${branding.secondaryColor} 0%, ${branding.primaryColor} 100%)` }
        : {};

    if (isMobile) {
      return (
        <div style={{ position: 'relative', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
          {(!hasSplash && !isCustomTenant)
            ? <video autoPlay muted loop playsInline
                style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', objectFit: 'cover', zIndex: 0 }}
                src="/login-bg.mp4"
              />
            : <div style={{ position: 'fixed', inset: 0, zIndex: 0, ...mobileBg }} />
          }
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(8,20,14,0.75)', zIndex: 1 }} />
          <div style={{ position: 'relative', zIndex: 2, flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            {formPanel(title, subtitle, content)}
          </div>
        </div>
      );
    }
    return (
      <div style={{ minHeight: '100vh', background: '#07110D', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <div style={{
          display: 'flex', width: '100%', maxWidth: 900,
          borderRadius: 20, overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.6)',
          border: '1px solid rgba(255,255,255,0.07)',
          minHeight: 560,
        }}>
          {formPanel(title, subtitle, content)}
          {rightPanel}
        </div>
      </div>
    );
  };

  if (screen === 'mfa') return layout(
    'Check your email',
    <>We sent a 6-digit code to <strong style={{ color: '#fff' }}>{email}</strong></>,
    <form onSubmit={handleMfa}>
      <DarkInput label="Verification code" value={otp} onChange={setOtp} placeholder="000000" required />
      {submitBtn('Verify')}
      {backBtn('login')}
    </form>
  );

  if (screen === 'forgot') return layout(
    'Reset your password',
    "Enter your email and we'll send you a reset link.",
    <form onSubmit={handleForgot}>
      <DarkInput label="Email" value={email} onChange={setEmail} type="email" placeholder="you@example.com" required />
      {submitBtn('Send reset link')}
      {backBtn('login')}
    </form>
  );

  if (screen === 'forgotSent') return layout(
    'Check your inbox',
    <>If <strong style={{ color: '#fff' }}>{email}</strong> is registered, a reset link is on its way.</>,
    <button onClick={() => setScreen('login')} style={{ width: '100%', padding: '14px', borderRadius: 10, background: `linear-gradient(135deg, ${branding.buttonColor ?? branding.primaryColor}, ${branding.buttonColor ?? branding.secondaryColor})`, color: '#fff', fontWeight: 700, fontSize: 15, border: 'none', cursor: 'pointer' }}>
      Back to sign in
    </button>
  );

  const isLogin = screen === 'login';

  return layout(
    isLogin ? 'Sign in to your account' : 'Create your account',
    isLogin
      ? <>Don't have an account?{' '}
          <button type="button" onClick={() => { setScreen('register'); setError(''); }}
            style={{ color: 'var(--color-accent, #4ADE80)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 0, fontWeight: 600 }}>
            Sign up free
          </button></>
      : <>Already have an account?{' '}
          <button type="button" onClick={() => { setScreen('login'); setError(''); }}
            style={{ color: 'var(--color-accent, #4ADE80)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 0, fontWeight: 600 }}>
            Sign in
          </button></>,
    <>
      <div style={{ display: 'flex', marginBottom: 32, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        {(['login', 'register'] as const).map(t => (
          <button key={t} type="button" onClick={() => { setScreen(t); setError(''); }}
            style={{
              flex: 1, padding: '10px', background: 'none', border: 'none',
              borderBottom: `2px solid ${screen === t ? branding.primaryColor : 'transparent'}`,
              color: screen === t ? 'var(--color-accent, #4ADE80)' : 'rgba(255,255,255,0.7)',
              fontWeight: screen === t ? 700 : 400, fontSize: 13, cursor: 'pointer',
              marginBottom: -1, transition: 'all 0.2s',
            }}>
            {t === 'login' ? 'Sign in' : 'Create account'}
          </button>
        ))}
      </div>

      {isLogin ? (
        <form onSubmit={handleLogin}>
          <DarkInput label="Email" value={email} onChange={setEmail} type="email" placeholder="you@example.com" required />
          <DarkInput label="Password" value={password} onChange={setPassword} type="password" placeholder="••••••••" required />
          <div style={{ textAlign: 'right', marginTop: -12, marginBottom: 28 }}>
            <button type="button" onClick={() => { setScreen('forgot'); setError(''); }}
              style={{ color: 'var(--color-accent, #4ADE80)', background: 'none', border: 'none', fontSize: 12, cursor: 'pointer', padding: 0 }}>
              Forgot password?
            </button>
          </div>
          {submitBtn('Sign in')}

        </form>
      ) : (
        <form onSubmit={handleRegister}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 24 }}>
            {(['Customer', 'Contractor'] as const).map(r => (
              <button key={r} type="button" onClick={() => setRegRole(r)} style={{
                flex: 1, padding: '10px', borderRadius: 8,
                border: `2px solid ${regRole === r ? branding.primaryColor : 'rgba(255,255,255,0.12)'}`,
                background: regRole === r ? `${branding.primaryColor}26` : 'transparent',
                color: regRole === r ? 'var(--color-accent, #4ADE80)' : 'rgba(255,255,255,0.75)',
                fontWeight: 600, fontSize: 13, cursor: 'pointer',
              }}>
                {r === 'Customer' ? '👤 Customer' : `🚚 ${contractorLabel}`}
              </button>
            ))}
          </div>
          {regRole === 'Contractor' && !CONTRACTOR_SELF_REGISTER_ENABLED ? (
            <div>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.6, marginBottom: 20 }}>
                We're onboarding {contractorLabel.toLowerCase()}s by application right now. Send us your name, phone,
                city/area, and vehicle, and we'll follow up.
              </p>
              <a
                href={`mailto:support@box-runner.net?subject=${encodeURIComponent(`Become a ${contractorLabel}`)}&body=${encodeURIComponent('Name: \nPhone: \nCity/area: \nVehicle: \n')}`}
                style={{
                  display: 'block', textAlign: 'center', padding: '12px', borderRadius: 8,
                  background: branding.primaryColor, color: '#fff', fontWeight: 600, fontSize: 14,
                  textDecoration: 'none',
                }}
              >
                Apply to be a {contractorLabel}
              </a>
            </div>
          ) : (
            <>
              <DarkInput label="Full name" value={regName} onChange={setRegName} required />
              <DarkInput label="Email" value={email} onChange={setEmail} type="email" required />
              <DarkInput label="Password" value={password} onChange={setPassword} type="password" required />
              <DarkInput label="Phone (optional)" value={regPhone} onChange={setRegPhone} type="tel" />
              {regRole === 'Contractor' && <DarkInput label="Vehicle description" value={regVehicle} onChange={setRegVehicle} placeholder="e.g. 2022 Ram 1500 – White" />}
              {submitBtn('Create account')}
            </>
          )}
        </form>
      )}
    </>
  );
}
